import React, { useEffect, useState } from "react";
import axios from "axios";
import styles from "../../assets/styles/yogateachers/Teachers.module.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${API_BASE}/api/about-teacher`;

const resolveAssetUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${API_BASE}${value}`;
  if (value.startsWith("uploads/")) return `${API_BASE}/${value}`;
  return `${API_BASE}/uploads/${value}`;
};

// Extract YouTube video ID from various URL formats
const getYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
    /^([A-Za-z0-9_-]{11})$/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Get YouTube thumbnail URL
const getYouTubeThumbnail = (videoId) =>
  `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

const Teachers = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Track which video card is "playing" (by index)
  const [playingIndex, setPlayingIndex] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(API);
      const payload = Array.isArray(res.data?.data)
        ? res.data.data[0]
        : Array.isArray(res.data)
        ? res.data[0]
        : res.data?.data || res.data;
      setData(payload || null);
    } catch (err) {
      console.error("API ERROR 👉", err);
      setError("Failed to load teachers page data.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;
  if (!data) return <p>No Data Found...</p>;

  const teachers = data.teacherSection?.teachers || [];
  const videos = data.videoSection?.videos || [];

  return (
    <div className={styles.container}>

      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <p className={styles.quoteText}>{data.banner?.mainSubHeading}</p>
          <h1 className={styles.mainTitle}>{data.banner?.heading}</h1>
          <div className={styles.breadcrumb}>
            <span>Home</span> / <span>Our Teachers</span>
          </div>
        </div>
      </section>

      {/* Teachers Section */}
      <section className={styles.facultySection}>
        <div className={styles.facultyHeader}>
          <p className={styles.facultyTitle}>
            {data.teacherSection?.teacherHeading}
          </p>
          <h2 className={styles.facultySubtitle}>
            {data.teacherSection?.teacherMainHeading}
          </h2>
          <p className={styles.facultyDescription}>
            {data.teacherSection?.teacherMainPara}
          </p>
        </div>

        <div className={styles.teachersGrid}>
          {teachers.map((teacher, index) => (
            <div key={index} className={styles.teacherCard}>

              {/* LEFT — Info */}
              <div className={styles.teacherInfo}>
                <h3 className={styles.teacherName}>{teacher.teacherName}</h3>

                {/* Role Tags */}
                <div className={styles.teacherTags}>
                  {Array.isArray(teacher.role)
                    ? teacher.role.map((tag, i) => (
                        <span key={i} className={styles.tag}>{tag}</span>
                      ))
                    : teacher.role?.split(",").map((tag, i) => (
                        <span key={i} className={styles.tag}>{tag}</span>
                      ))}
                </div>

                {/* Description */}
                <div className={styles.teacherBio}>
                  <p>{teacher.description}</p>
                </div>

                {/* Certification */}
                <div className={styles.infoSection}>
                  <h4 className={styles.infoTitle}>Certification</h4>
                  <ul>
                    {teacher.certification?.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>

                {/* Experience */}
                <div className={styles.infoSection}>
                  <h4 className={styles.infoTitle}>Experience</h4>
                  <ul>
                    {teacher.experience?.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>

                {/* Expertise */}
                <div className={styles.infoSection}>
                  <h4 className={styles.infoTitle}>Expertise</h4>
                  <ul>
                    {teacher.expertise?.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* RIGHT — Image + YouTube Button (centered) */}
              <div className={styles.teacherImageSection}>
                <div className={styles.teacherImageWrapper}>
                  <img
                    src={resolveAssetUrl(teacher.teacherImage)}
                    alt={teacher.teacherName}
                    className={styles.teacherImage}
                  />

                  {/* YouTube Button — only if a youtubeUrl is provided */}
                  {teacher.youtubeUrl && (
                    <a
                      href={teacher.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.youtubeBtn}
                      aria-label={`Watch ${teacher.teacherName} on YouTube`}
                    >
                      {/* YouTube play icon SVG */}
                      <svg
                        className={styles.youtubeBtnIcon}
                        viewBox="0 0 68 48"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"
                          fill="#ff0000"
                        />
                        <path d="M45 24 27 14v20" fill="#fff" />
                      </svg>
                      <span className={styles.youtubeBtnText}>Watch on YouTube</span>
                    </a>
                  )}
                </div>
              </div>

            </div>
          ))}
        </div>
      </section>

      {/* VIDEO SECTION */}
      <section className={styles.section}>
        <div className={styles.heading}>
          <span>{data.videoSection?.videoHeading}</span>
          <h2>{data.videoSection?.videoMainHeading}</h2>
        </div>

        <div className={styles.videoGrid}>
          {videos.map((video, index) => {
            const youtubeId = getYouTubeId(video.video);
            const isPlaying = playingIndex === index;

            if (youtubeId) {
              // YouTube card — thumbnail + play button, then embed on click
              return (
                <div key={index} className={styles.card}>
                  {isPlaying ? (
                    <iframe
                      className={styles.videoIframe}
                      src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
                      title={`Teacher video ${index + 1}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div
                      className={styles.videoThumbWrapper}
                      onClick={() => setPlayingIndex(index)}
                    >
                      <img
                        src={
                          resolveAssetUrl(video.thumbnail) ||
                          getYouTubeThumbnail(youtubeId)
                        }
                        alt={`Video ${index + 1}`}
                        className={styles.videoThumb}
                        onError={(e) => {
                          e.target.src = getYouTubeThumbnail(youtubeId);
                        }}
                      />
                      {/* Play button overlay */}
                      <button
                        className={styles.playBtn}
                        aria-label="Play YouTube video"
                      >
                        <svg
                          viewBox="0 0 68 48"
                          xmlns="http://www.w3.org/2000/svg"
                          className={styles.playBtnIcon}
                        >
                          <path
                            d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"
                            fill="#ff0000"
                          />
                          <path d="M45 24 27 14v20" fill="#fff" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              );
            }

            // Fallback: regular video file (mp4/webm)
            return (
              <div key={index} className={styles.card}>
                <video
                  src={resolveAssetUrl(video.video)}
                  controls
                  width="100%"
                  className={styles.videoIframe}
                />
              </div>
            );
          })}
        </div>
      </section>

    </div>
  );
};

export default Teachers;